import type { CheckoutRecord } from '../db/checkout-record.ts';
import type { CheckoutStore } from '../db/indexeddb.ts';
import type { CheckoutSendResult } from './checkout.ts';
import { backoffDelayMs, nextAttemptAt } from './retry.ts';

/**
 * G-013 — retry pending check-outs.
 *
 * Deliberately a separate class from `RetryEngine` rather than a feature added
 * to it: that class is the one thing standing between a participant's check-in
 * and losing it, and it is tested as such. Widening it to a second record
 * shape, a second store and a second result type would put every one of those
 * guarantees back in play to buy a loop that fits on one screen. So check-outs
 * reuse exactly one thing from it — `nextAttemptAt`, and through it the §10
 * backoff schedule — and nothing else. That sharing is the part that matters:
 * a device must never run two different retry rhythms, because the whole point
 * of the schedule is that every phone in the venue backs off together instead
 * of hammering a network the venue already cannot spare.
 *
 * Like `RetryEngine`, recovery is driven by events the page can observe —
 * startup, coming online, becoming visible — because Background Sync does not
 * exist on iOS Safari. There is no poll here: a check-out is not on the
 * critical path of the participant's day the way a check-in is, and Task 12's
 * UI owns any manual "send now" affordance.
 *
 * The one exception to "events only" is the self-heal below, which exists
 * because one real failure fires no event at all.
 */

/**
 * How many consecutive failed reads will still book a self-heal pass.
 *
 * Read the counter precisely, because it is easy to assume otherwise: it
 * counts *failed reads*, whatever triggered them — startup, `online`,
 * `visibilitychange`, a timer this class set, or a direct `runOnce()`. Five
 * `visibilitychange` fires during one blocked-database outage therefore spend
 * the budget without a single timer ever running. Any read that succeeds
 * resets it to zero.
 *
 * And note what the cap gates: only whether a *new timer is scheduled*. It
 * never gates reading. Once the budget is gone this class stops waking the
 * device on its own, but the next `online` or `visibilitychange` still drives
 * a full pass and still recovers the queue. Exhausting it is going quiet, not
 * giving up.
 *
 * Bounded, not endless, and the reason is the shape of the failure. A pass
 * that cannot even read the queue is almost always a blocked IndexedDB
 * upgrade: an old tab still holds the previous database version. That clears
 * itself within seconds to minutes, when the stale tab closes — and *no event
 * fires at that moment*. Not `online`, not `visibilitychange`. So a handful of
 * attempts spaced by the shared backoff (2s, 5s, 10s, 20s, 30s — a bit over a
 * minute in total) covers the whole window in which the condition realistically
 * resolves. Retrying past that would drain a battery in a pocket for a
 * condition that is no longer going to change on its own, at an event where
 * the phone also has to survive the afternoon.
 */
export const MAX_SELF_HEAL_ATTEMPTS = 5;

export interface CheckoutRetryDeps {
  store: CheckoutStore;
  send: (record: CheckoutRecord) => Promise<CheckoutSendResult>;
  /** Only check-outs for the event currently on screen are retried. */
  eventId?: string;
  now?: () => number;
  isOnline?: () => boolean;
  isVisible?: () => boolean;
  /** Called after every attempt so the UI can follow the state machine. */
  onResult?: (result: CheckoutSendResult) => void;
  /** Told about a pass that could not even read the queue. See `runOnce`. */
  onError?: (error: unknown) => void;
  /**
   * Where the `online` and `visibilitychange` events come from. Injected so a
   * test can dispatch them, and so this module never assumes a `window`.
   */
  onlineTarget?: EventTarget;
  visibilityTarget?: EventTarget;
}

export class CheckoutRetry {
  readonly #deps: CheckoutRetryDeps;
  readonly #now: () => number;
  readonly #isOnline: () => boolean;
  readonly #isVisible: () => boolean;
  readonly #teardown: (() => void)[] = [];

  #running = false;
  #started = false;
  /** Honours a server `Retry-After` across the whole queue, not one record. */
  #pauseUntil = 0;
  /** Consecutive failed reads. Reset by any read that succeeds. */
  #selfHealAttempts = 0;
  #selfHealTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(deps: CheckoutRetryDeps) {
    this.#deps = deps;
    this.#now = deps.now ?? (() => Date.now());
    this.#isOnline = deps.isOnline ?? (() => navigator.onLine);
    this.#isVisible = deps.isVisible ?? (() => document.visibilityState === 'visible');
  }

  /** Startup trigger plus listeners for the other two. */
  start(): void {
    if (this.#started) return;
    this.#started = true;

    const online = () => {
      this.#trigger();
    };
    const visibility = () => {
      if (this.#isVisible()) this.#trigger();
    };

    const onlineTarget = this.#deps.onlineTarget ?? globalThis;
    const visibilityTarget = this.#deps.visibilityTarget ?? globalThis.document;

    onlineTarget.addEventListener('online', online);
    visibilityTarget.addEventListener('visibilitychange', visibility);
    // Teardown closures rather than re-deriving the targets in `stop()`: the
    // deps could hand back a different object on a second read, and then
    // `stop()` would silently leave the real listeners attached.
    this.#teardown.push(
      () => {
        onlineTarget.removeEventListener('online', online);
      },
      () => {
        visibilityTarget.removeEventListener('visibilitychange', visibility);
      },
    );

    this.#trigger();
  }

  stop(): void {
    for (const undo of this.#teardown.splice(0)) undo();
    // A scheduled self-heal is as much a thing `start()` left running as a
    // listener is, so `stop()` has to take it back or the class would keep
    // waking a page that asked to be left alone.
    this.#cancelSelfHeal();
    this.#selfHealAttempts = 0;
    this.#started = false;
  }

  /**
   * Ask for one more pass later, because nothing else will.
   *
   * `online` and `visibilitychange` cover a device that regains signal or a
   * participant who comes back to the tab. Neither covers the stale tab that
   * was blocking the database finally closing — that moment is silent, and
   * without this timer the queue would sit there recoverable and unasked
   * until the participant happened to background and foreground the page,
   * which nobody knows to do.
   */
  #scheduleSelfHeal(): void {
    if (this.#selfHealTimer !== null) return;
    const delay = backoffDelayMs(this.#selfHealAttempts);
    this.#selfHealTimer = setTimeout(() => {
      this.#selfHealTimer = null;
      this.#trigger();
    }, delay);
  }

  #cancelSelfHeal(): void {
    if (this.#selfHealTimer === null) return;
    clearTimeout(this.#selfHealTimer);
    this.#selfHealTimer = null;
  }

  /**
   * Fire a pass from a place that cannot await it.
   *
   * `runOnce` already absorbs the failure modes it knows about; this catch
   * exists so that an *unknown* one cannot become an unhandled rejection
   * escaping a DOM event listener, where it would be invisible to the page and
   * could take the listener down with it.
   */
  #trigger(): void {
    void this.runOnce().catch((error: unknown) => {
      this.#deps.onError?.(error);
    });
  }

  /**
   * One pass over the pending check-outs.
   *
   * Sequential by design: parallel sends would fire several Turnstile
   * challenges at once and compete for the same few kilobits of venue Wi-Fi.
   * Re-entrancy is refused rather than queued — the next trigger picks up
   * whatever this pass leaves behind.
   */
  async runOnce(): Promise<void> {
    if (this.#running) return;
    if (!this.#isOnline()) return;
    if (this.#now() < this.#pauseUntil) return;

    this.#running = true;
    try {
      let pending: CheckoutRecord[];
      try {
        pending = await this.#deps.store.listPending(this.#deps.eventId);
      } catch (error) {
        /*
         * Swallowed on purpose, and this is the one place it is correct to.
         *
         * The realistic cause is a blocked IndexedDB upgrade: an old tab still
         * holds the previous database version, so opening it rejects. That
         * condition clears itself the moment the stale tab closes — but only
         * if something tries again. Rethrowing would turn a self-healing wait
         * into a dead retry loop, and because `start()` and both listeners
         * call this without awaiting, it would surface as an unhandled
         * rejection nobody at the venue can act on. Nothing is lost: the
         * records are still on disk, still pending, and the next trigger reads
         * them again.
         *
         * Swallowing is necessary but not sufficient, though — the loop would
         * be able to recover and never be asked to, because the stale tab
         * closing fires no event. So the pass books its own next attempt, on
         * the shared backoff and capped at `MAX_SELF_HEAL_ATTEMPTS`.
         */
        this.#deps.onError?.(error);
        this.#selfHealAttempts += 1;
        if (this.#selfHealAttempts <= MAX_SELF_HEAL_ATTEMPTS) this.#scheduleSelfHeal();
        return;
      }

      // The database answered, so whatever was blocking it is gone and the
      // budget is spent on the *next* outage rather than carried into it.
      this.#selfHealAttempts = 0;
      this.#cancelSelfHeal();

      for (const record of pending) {
        if (!this.#isOnline()) break;
        if (this.#now() < nextAttemptAt(record)) continue;

        const result = await this.#deps.send(record);
        this.#deps.onResult?.(result);

        if (result.kind === 'pending' && result.retryAfterSeconds !== undefined) {
          // The server asked the whole device to wait, not just this record.
          this.#pauseUntil = this.#now() + result.retryAfterSeconds * 1000;
          break;
        }

        // A failed challenge stops the pass: generating challenges back to
        // back is forbidden, and the participant is the one who has to solve
        // it before anything else can go out.
        if (result.kind === 'verify_retry') break;
      }
    } finally {
      this.#running = false;
    }
  }
}
